import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@/environments/environment';
import {
  GetAssetNetworksResponse,
  GetAssetNetworksQuery,
  GetNetworksResponse,
  GetWhitelistAddressesResponse,
  GetWhitelistAddressesMinimalResponse,
  WhitelistAddressSort,
  BalanceHistoryResponse,
  BalanceHistoryPeriod,
} from '../models/asset-management.model';

/**
 * All HTTP calls in this service return the standard
 * `ApiResponse<T>` envelope (data + pagination at the root for paginated lists).
 */
@Injectable({
  providedIn: 'root',
})
export class AssetManagementService {
  private readonly baseUrl = `${environment.assetManagementApiUrl}/api/v1`;
  constructor(private http: HttpClient) {}

  getNetworks(): Observable<GetNetworksResponse> {
    return this.http.get<GetNetworksResponse>(`${this.baseUrl}/network`);
  }

  getAssetNetworks(
    query: GetAssetNetworksQuery
  ): Observable<GetAssetNetworksResponse> {
    return this.http.get<GetAssetNetworksResponse>(
      `${this.baseUrl}/asset/asset-networks`,
      {
        params: {
          ...(query.assetId ? { assetId: query.assetId } : {}),
          ...(query.networkId ? { networkId: query.networkId } : {}),
          ...(query.page ? { page: query.page.toString() } : {}),
          ...(query.limit ? { limit: query.limit.toString() } : {}),
          ...(query.orderBy ? { orderBy: query.orderBy } : {}),
          ...(query.order ? { order: query.order } : {}),
        },
      }
    );
  }

  getWhitelistAddresses(
    page: number = 1,
    limit: number = 10,
    sort?: WhitelistAddressSort,
    search?: string
  ): Observable<GetWhitelistAddressesResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('orderBy', sort ? sort.sortBy : '')
      .set('order', sort ? sort.sortDirection : '')
      .set('search', search ?? '');

    return this.http
      .get<GetWhitelistAddressesResponse>(
        `${this.baseUrl}/network/whitelist-addresses`,
        {
          params,
        }
      )
      .pipe(
        map(response => ({
          ...response,
          data: response.data ?? [],
        }))
      );
  }

  /**
   * `GET /api/v1/network/whitelist-addresses/minimal` — narrowed projection
   * filtered by a single asset-network. Used by the wallet-detail
   * "connect whitelist address" modal to populate its address dropdown
   * once the user has picked a network, so the list only contains
   * addresses that actually belong to the selected network.
   */
  getWhitelistAddressesMinimal(
    assetNetworkId: string
  ): Observable<GetWhitelistAddressesMinimalResponse> {
    const params = new HttpParams().set('assetNetworkId', assetNetworkId);

    return this.http
      .get<GetWhitelistAddressesMinimalResponse>(
        `${this.baseUrl}/network/whitelist-addresses/minimal`,
        { params }
      )
      .pipe(
        map(response => ({
          ...response,
          data: response.data ?? [],
        }))
      );
  }
  /**
   * Get balance history for a vault
   * @param walletId - The wallet ID (optional, empty string for all wallets)
   * @param period - The period to fetch history for (WEEK, MONTH, 2MONTH)
   */
  getBalanceHistory(
    vaultId: string,
    walletId: string[],
    period: BalanceHistoryPeriod
  ): Observable<BalanceHistoryResponse> {
    return this.http.get<BalanceHistoryResponse>(
      `${this.baseUrl}/vault/${vaultId}/wallet/balance-history?walletId=${walletId.join(',')}&period=${period}`
    );
  }
}
